import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRoute, useLocation } from 'wouter';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdLoader } from '@commander-services/gui-components';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import Form from './Form';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { CmdTable } from '../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { REQUIRED } from '../CmdTable/constants';
import {
  VEHICLE_SETTINGS_CUSTOMER_ID_KEY,
  VEHICLE_SETTINGS_LICENCE_NUMBER,
  VEHICLE_SETTINGS_SETTINGS_SAVE,
  VEHICLE_SETTINGS_TABLE_KEY,
  VEHICLE_SETTINGS_URL,
  PeriodLockDefinitionTranslationKeys,
  VEHICLE_SETTINGS_DETAIL_KEY,
  VEHICLE_SETTINGS_MANUFACTURER,
} from './constants';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import useVehicleSettings from './useVehicleSettings';
import { VehicleRecord, VehicleResponse } from './types';
import { URL_VEHICLES_SETTINGS } from '../../router/constants';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import { arrayOrder, getTableName, getTableSettings } from '../CmdTable/CmdTableService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';

const fetchSize = 20;

export default function VehiclesSettings() {
  const { formatMessage: f } = useIntl();
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${VEHICLE_SETTINGS_URL}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(VEHICLE_SETTINGS_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(VEHICLE_SETTINGS_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(VEHICLE_SETTINGS_TABLE_KEY)
  );
  const query = useUrlQueryParams();
  const customerIdUrl = query.get('customerId');
  const { columns, columnOrder, columnVisibility } = useVehicleSettings();
  const { trackEvent } = useAnalytics();

  const handleClose = React.useCallback(() => {
    if (pathname.includes('add') || pathname.includes('edit')) navigate(`${VEHICLE_SETTINGS_URL}`);
    setIsSubpageOpened(false);
    trackEvent([`table ${getTableName(VEHICLE_SETTINGS_TABLE_KEY)}`, 'click', 'Close subpage']);
  }, [navigate, pathname]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [VEHICLE_SETTINGS_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  React.useEffect(() => {
    if (pathname === URL_VEHICLES_SETTINGS) {
      setIsSubpageOpened(false);
      const documentTitle = `${f({ id: 'tab.vehicle' })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({ id: 'tab.vehicle' })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
      setIsSubpageOpened(true);
    }
    // return () => {
    //   setisSubpageOpened(true);
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === VEHICLE_SETTINGS_CUSTOMER_ID_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
          system: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(VEHICLE_SETTINGS_TABLE_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns);
      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${VEHICLE_SETTINGS_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];
  const isLoading = isLoadingSettings;

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div>
            {isSubpageOpened && params ? (
              <Form
                id={Number(params.id)}
                customerId={customerIdUrl ? Number(customerIdUrl) : null}
                updateTable={updateTable}
                handleClose={handleClose}
              />
            ) : (
              <div>
                <div className="page-header">
                  <h4 className="page-header__title">
                    {f({ id: 'menu.settings' })} / {f({ id: 'tab.vehicle' })}
                    <OnlineHelp topic="vehicles" />
                  </h4>
                </div>

                <div className="container-fluid">
                  <section className="basic-box border-top-thin border-top-gray">
                    <div className="basic-box__content">
                      <div className="row no-gutters mb-2">
                        <div className="col-12 col-md-auto mr-md-2 mb-2" />
                        <div className="col col-md" />
                        <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                        <div className="col-auto order-4 order-lg-5">
                          <CmdTableSettings
                            tableId={VEHICLE_SETTINGS_TABLE_KEY}
                            tableName={VEHICLE_SETTINGS_SETTINGS_SAVE}
                            sort={{ column: VEHICLE_SETTINGS_CUSTOMER_ID_KEY, direction: 'desc' }}
                            translationKeys={PeriodLockDefinitionTranslationKeys}
                          />
                        </div>
                      </div>
                      <div className="d-flex flex-wrap mb-2">
                        {tableFiltersKey &&
                          tableFiltersKey.map((filterId) => (
                            <FilterChips
                              key={filterId}
                              id={filterId}
                              tableName={VEHICLE_SETTINGS_TABLE_KEY}
                            />
                          ))}
                      </div>
                      {isLoading && <CmdLoader inContent={true} />}
                      <CmdTable<VehicleResponse, VehicleRecord>
                        name={VEHICLE_SETTINGS_TABLE_KEY}
                        // fetchSize={fetchSize}
                        stickyColumns={[VEHICLE_SETTINGS_DETAIL_KEY]}
                        lockedColumns={[
                          VEHICLE_SETTINGS_CUSTOMER_ID_KEY,
                          VEHICLE_SETTINGS_LICENCE_NUMBER,
                          VEHICLE_SETTINGS_MANUFACTURER,
                        ]}
                        columns={columns}
                        columnOrder={columnOrder}
                        columnVisibility={columnVisibility}
                      />
                    </div>
                  </section>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
